import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Church, MapPin, Users, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { getInviteInfo, acceptInvite } from "@/lib/api";
import { toast } from "sonner";

export default function JoinParishPage({ user }) {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadInviteInfo();
  }, [inviteCode]);

  const loadInviteInfo = async () => {
    setLoading(true);
    try {
      const res = await getInviteInfo(inviteCode);
      setInvite(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid or expired invite link");
    }
    setLoading(false);
  };

  const handleAcceptInvite = async () => {
    setJoining(true);
    try {
      const res = await acceptInvite(inviteCode);
      setSuccess(true);
      toast.success(res.data.message);
      // Redirect to parish page after 2 seconds
      setTimeout(() => {
        window.location.href = "/parish";
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to join parish");
    }
    setJoining(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-[var(--liturgical-primary)] animate-spin mx-auto mb-3" />
          <p className="text-sm text-[#6B665E] font-['Nunito']">Loading invite...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center px-4">
        <Card className="bg-white border-[#E5E0D5] shadow-md max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="font-['Crimson_Text'] text-2xl font-bold text-[#2D2A26] mb-2">
              Invalid Invite Link
            </h1>
            <p className="text-sm text-[#6B665E] font-['Nunito'] mb-6">
              {error}
            </p>
            <Button 
              onClick={() => navigate("/parish")}
              className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
            >
              Browse Parishes
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center px-4">
        <Card className="bg-white border-[#E5E0D5] shadow-md max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="font-['Crimson_Text'] text-2xl font-bold text-[#2D2A26] mb-2">
              Welcome to {invite?.parish?.name}!
            </h1>
            <p className="text-sm text-[#6B665E] font-['Nunito'] mb-4">
              You are now a member of this parish community.
            </p>
            <p className="text-xs text-[#6B665E] font-['Nunito']">
              Redirecting to your parish page...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center px-4" data-testid="join-parish-page">
      <Card className="bg-white border-[#E5E0D5] shadow-md max-w-md w-full">
        <CardContent className="p-0">
          {/* Parish Image Header */}
          <div className="relative h-40 bg-[#2D2A26] rounded-t-lg overflow-hidden">
            {invite?.parish?.image_url ? (
              <img 
                src={invite.parish.image_url} 
                alt={invite.parish.name}
                className="w-full h-full object-cover opacity-80"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Church className="w-16 h-16 text-white/50" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              <h1 className="font-['Crimson_Text'] text-2xl font-bold text-white">
                {invite?.parish?.name}
              </h1>
              <p className="text-sm text-white/80 font-['Nunito'] flex items-center gap-1 mt-1">
                <MapPin className="w-3 h-3" />
                {invite?.parish?.address || "Location not available"}
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="text-center mb-6">
              <p className="text-sm text-[#6B665E] font-['Nunito'] mb-2">
                You've been invited to join
              </p>
              <p className="text-sm text-[#2D2A26] font-['Nunito']">
                {invite?.parish?.description}
              </p>
            </div>

            {/* Stats */}
            <div className="flex items-center justify-center gap-6 mb-6 py-3 border-y border-[#E5E0D5]">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-[var(--liturgical-primary)]">
                  <Users className="w-4 h-4" />
                  <span className="font-['Nunito'] font-semibold">{invite?.parish?.member_count || 0}</span>
                </div>
                <p className="text-xs text-[#6B665E] font-['Nunito']">Members</p>
              </div>
            </div>

            {/* User already in parish check */}
            {user?.parish_id === invite?.parish?.parish_id ? (
              <div className="text-center">
                <p className="text-sm text-green-600 font-['Nunito'] mb-4">
                  You're already a member of this parish!
                </p>
                <Button 
                  onClick={() => navigate("/parish")}
                  className="w-full bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
                >
                  Go to My Parish
                </Button>
              </div>
            ) : user?.parish_id ? (
              <div className="text-center">
                <p className="text-sm text-amber-600 font-['Nunito'] mb-4">
                  You're already a member of another parish. You'll need to leave your current parish before joining a new one.
                </p>
                <Button 
                  onClick={() => navigate("/parish")}
                  variant="outline"
                  className="w-full border-[var(--liturgical-primary)] text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/10 font-['Nunito']"
                >
                  Go to My Parish
                </Button>
              </div>
            ) : (
              <Button 
                onClick={handleAcceptInvite}
                disabled={joining}
                className="w-full bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] font-semibold py-5"
                data-testid="accept-invite-btn"
              >
                {joining ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Joining...
                  </>
                ) : (
                  <>
                    <Church className="w-4 h-4 mr-2" />
                    Join {invite?.parish?.name}
                  </>
                )}
              </Button>
            )}

            <p className="text-[10px] text-center text-[#6B665E] font-['Nunito'] mt-4">
              By joining, you'll be able to participate in this parish's community, events, and marketplace.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
